import { type PaymentMethodType } from '@hooks-api/types/dataModel/payment'
import { Box } from '@pets-ui/Box/Box'
import { Button } from '@pets-ui/Button/Button'
import { colors } from '@pets-ui/tokens/colors.stylex'
import * as stylex from '@stylexjs/stylex'
import { useEffect } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory, useParams } from 'react-router-dom-v5'
import Amount from '../components/amount'
import QRcode from '../components/qr-code'
import { QRcodeOrderInfo } from '../components/qrcode-order-info'
import { useCampaign } from '../hooks/use-campaign'
import { useClientPaymentMethod } from '../hooks/use-client-payment-method'
import { useOrderPurchaseMessage } from '../hooks/use-order-puchase-message'
import { getPurchaseStatusClasses } from '../model/order-model'
import style from '../style/components/qrcode-order-status.module.scss'

const baseStyle = stylex.create({
  changePaymentButton: {
    borderColor: colors.white,
    borderRadius: '100px',
    color: colors.white,
    marginTop: 8,
  },
})

interface Props {
  payment: PaymentMethodType
  status: string
  hasGiveaways: boolean
  isOfflinePayment: boolean
  amount: number
  campaignID: string
  url: string
  orderID: string
  isDispatch: boolean
}
const QRcodeOrderStatus = (props: Props) => {
  const {
    payment,
    status,
    hasGiveaways,
    isOfflinePayment,
    amount,
    url,
    orderID,
    isDispatch,
  } = props
  const { t } = useTranslation()
  const history = useHistory()
  const { getI18nPaymentMethod } = useClientPaymentMethod()
  const { campaignCodeFromUrl } = useCampaign()
  const { orderHash } = useParams<{ orderHash: string }>()

  const orderPurchaseMessage = useOrderPurchaseMessage(status)

  const purchaseStatusClasses = getPurchaseStatusClasses(status, style)

  // NOTE: Sprint 2504 實作
  const handleChangePayment = () => {
    history.replace(`/campaigns/${campaignCodeFromUrl}/payments?orderHash=${orderHash}&changePayment=true`)
  }

  /**
   * 三星瀏覽器的QRcode circle背景顏色不符，因為root背景顏色沒設定，導致瀏覽器dark mode color顏色不符
   * 透過設定root background color = 'white'來修正
   * [sc-32525]
   */
  useEffect(() => {
    const rootDom = document.getElementById('root')
    if (!rootDom) return
    rootDom.style.background = 'white'
  }, [])

  return (
    <section className={style.wrapper}>
      <div className={style.ticket}>
        <div className={style['ticket-content-wrapper']}>
          <QRcodeOrderInfo
            title={t('order.paymentStatus')}
            content={orderPurchaseMessage}
            contentClassName={purchaseStatusClasses}
          />
          {hasGiveaways
            ? (
              <QRcodeOrderInfo
                title={t('order.giftReceivingMethod')}
                content={t(
                  `qrcode.hasGiveaways.${Number(hasGiveaways)}.isDispatch.${Number(isDispatch)}`,
                )}
              />
            )
            : <Box width="100%" />}
          <QRcodeOrderInfo
            title={t('order.paymentMethod')}
            content={getI18nPaymentMethod(payment)}
            button={
              isOfflinePayment
                ? (
                  <Button
                    variant="outlined"
                    rootStyle={baseStyle.changePaymentButton}
                    onClick={handleChangePayment}
                  >
                    變更
                  </Button>
                )
                : null
            }
          />
          <QRcodeOrderInfo
            title="付款金額"
            content={<Amount amount={amount} fontSize={24} bold />}
          />
        </div>
        <hr className={style.line} />
        <div className={style['qrcode-content']}>
          <div className={style['tip-message']}>
            {t(
              `qrcodeMessage.hasGiveaways.${Number(hasGiveaways)}.isOffline.${Number(
                isOfflinePayment,
              )}`,
            )}
          </div>
          <div className={style.qrcode}>
            <QRcode url={url} size={144} />
          </div>
          <div className={style.orderID}>
            {t('order.parentOrderNumber')}
            &nbsp;
            {`#${orderID}`}
          </div>
        </div>
        <div className={`${style.circle} ${style['top-circle']}`} />
        <div className={`${style.circle} ${style['bottom-circle']}`} />
      </div>
    </section>
  )
}
export default QRcodeOrderStatus
